import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  GridValueGetterParams,
  GridColDef,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from "react-router-dom";
import Link from '@mui/material/Link';
import Typography from "@mui/material/Typography";
import Button from '@mui/material/Button/Button';
import Box from "@mui/material/Box/Box";

import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import CrossIcon from '@mui/icons-material/Close';

import {
  useAppDispatch,
  useAppSelector,
} from '../../store/hooks';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';


import CustomDataGrid from '../../components/DataGrid/DataGrid';
import BreadCrum from "../../components/Breadcrumbs/Breadcrumbs";
import { /* deleteUser,  */fetchSuppliers } from '../../store/supplierStore/supplierThunk';
import { /* IRole, */ ISupplierDetails } from "../../utils/supplier.types";
import { PermissionsEnum } from "../../utils/auth.types";
import { ApiStatus, apiLimit, defaultRowsPerPageOptions } from "../../utils/api.types";


import { Avatar, Card, CardContent, Grid, IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import { ICustomerDetails } from "../../utils/customer.types";
import DialogClientUpdateForm from "../Dialog/UpdateClientForm";
import DeleteIcon from '@mui/icons-material/Delete';
import { SearchIcon } from 'lucide-react';
import RemoveRedEyeTwoToneIcon from '@mui/icons-material/RemoveRedEyeTwoTone';


/**
 * Components to show customers list in the data grid.
 */
const SupplierList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { accountId = '' } = useParams();

  const { permissions, selectedAccount } = useAppSelector(state => state.auth);
  const { suppliers, status, rowCount } = useAppSelector(state => state.suppliers);

  //const canCreateUsers = permissions?.includes(PermissionsEnum.Users_Create);
  // const canUpdateUsers = permissions?.includes(PermissionsEnum.Users_Update);
  //const canViewUsers = permissions?.includes(PermissionsEnum.Users_View);
  // const canDeleteUsers = permissions?.includes(PermissionsEnum.Users_Delete);

  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const canUpdateAccount = permissions?.includes(PermissionsEnum.Account_Update);

  const [openCustomerDrawer, setOpenCustomerDrawer] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>('');

  const [filterQuery, setFilterQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // handle pagination
  const [page, setPage] = useState(0); // Current page (0-based index)
  const [pageSize, setPageSize] = useState(apiLimit.limit); // Rows per page
  const [pageQueryString, setPageQueryString] = useState(`page=0`); // Current page (0-based index)
  const [pageSizeQueryString, setPageSizeQueryString] = useState(`&limit=${apiLimit.limit}`); // Rows per page

  const handleViewClick = useCallback(({ _id }: ICustomerDetails) => {
    //Call view function
    let navLink = `/customer/view/${_id}`;
    navigate(navLink);
  }, []);

  /**
   * Fetch all the suppliers list on user's page load.
  */
  useEffect(() => {
    // Fetch account related suppliers if we are loading users on account details page.
    fetchSupplierList()
  }, [dispatch, selectedAccount, accountId, isEdit, filterQuery, page, pageSize, pageQueryString, pageSizeQueryString]);

  const fetchSupplierList = () => {
    const filter = (filterQuery && filterQuery !== '') ? `&name=${filterQuery}` : '';
    if (accountId) {
      dispatch(fetchSuppliers({ accountId: accountId, params: pageQueryString + pageSizeQueryString + filter }));
    } else if (selectedAccount) {
      const { _id: id } = selectedAccount;
      dispatch(fetchSuppliers({ accountId: id, params: pageQueryString + pageSizeQueryString + filter }));
    }
  }

  const handleOpenSupplierDrawer = (event: any, isSupplierEdit: boolean, row: ICustomerDetails | undefined) => {
    console.log("handleOpenCustomerDrawer called");
    console.log(row);
    console.log(isSupplierEdit);
    setIsEdit(isSupplierEdit);
    setSelectedCustomer(row);
    setOpenCustomerDrawer(true);
    event.stopPropagation();
  };

  const handleCloseCustomerDrawer = async (val: any) => {
    console.log("handleCloseCustomerDrawer called from updateClientForm")
    setIsEdit(false);
    setSelectedCustomer('');
    setOpenCustomerDrawer(false);
    if (val) {
      console.log("called handleCloseCustomerDrawer from fetchCustomersList")
      fetchSupplierList();
    }
    console.log("last end");
  };

  const cancelDialog = () => {
    // setIsClientLoading(false); 
    // setSelectedAgent(false); 
    // setIsAgentLoading(false); 
    // setIsLoading(false);
  }


  // Define Suppliers data grid column defs.
  const columns: GridColDef[] = useMemo(() => [
    {
      field: "legalName",
      headerName: 'Legal Name',
      type: "string",
      flex: 2,
      width: 800,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <Typography>
          <Grid container sx={{ justifyContent: 'left', alignItems: 'center', width: "500px" }} spacing={1}>
            <Grid item >
              <Avatar
                alt={value?.toUpperCase()}
                imgProps={{ "referrerPolicy": 'no-referrer' }}
                src={`${row.logo}`}
              />
            </Grid>
            <Grid item sx={{ justifyContent: 'center', alignItems: 'center' }} style={{ textAlign: "center" }}>
              <Tooltip title={value}>
                <Typography onClick={() => handleViewClick(row)}>{row.legalName} </Typography>
              </Tooltip>
            </Grid>
          </Grid>
        </Typography>
      )
    },
    {
      field: "taxIdentificationNumber2",
      headerName: 'GST',
      type: "string",
      flex: 1,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <Typography>{value}</Typography>
      )
    },
    {
      field: "contacts",
      headerName: 'Contact Name',
      type: "string",
      flex: 1,
      // width:300,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <>
          <Tooltip title={row.contactName}>
            <Typography>{row.contactName} <br /> {row.whatsAppNumber}</Typography>
          </Tooltip>
        </>
      )
    },
    {
      field: "status",
      headerName: 'Status',
      type: "string",
      flex: 1,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <Typography>
          <Grid container sx={{ justifyContent: 'left', alignItems: 'center', width: "500px" }} spacing={1}>
            <Grid item >
              <Tooltip title={row.isActive == true ? 'Active' : 'Inactive'}>
                <Typography>{row.isActive == true ? <CheckIcon color="success" /> : <CrossIcon color="error" />}</Typography>
              </Tooltip>
            </Grid>
            <Grid item sx={{ justifyContent: 'center', alignItems: 'center' }} style={{ textAlign: "center" }}>
              <Tooltip title={row.refToAccount ? 'Verfied' : 'Not Verified'}>
                <Typography>{row.refToAccount ? <CheckIcon color="success" /> : <CrossIcon color="error" />}</Typography>
              </Tooltip>
            </Grid>
          </Grid>
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: 'Actions',
      type: "string",
      flex: 1,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <>
          <IconButton
            style={{ color: '#488dff', backgroundColor: '#fff', border: '0.1px solid #488dff', height: 35, width: 35, marginRight: 10 }}
            size='small' onClick={() => handleViewClick(row)} ><RemoveRedEyeTwoToneIcon style={{ fontSize: 16 }} />

          </IconButton>
          {canUpdateAccount &&
            <IconButton
              style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35, marginRight: 10 }}
              size='small' onClick={(e) => handleOpenSupplierDrawer(e, true, row)}><EditIcon style={{ fontSize: 16 }} />
            </IconButton>
          }
        </>
      )
    },
    // {
    //   field: "actions",
    //   headerName: 'Actions',
    //   type: "actions",
    //   flex: 0.9,
    //   getActions: (params: GridValueGetterParams) => {
    //     const editAction = (
    //         <GridActionsCellItem
    //             icon={<EditIcon color="success"/>}
    //             title="Edit"
    //             label="Edit"
    //             onClick={(e) => handleOpenSupplierDrawer(e, true, params.row)} />
    //     );

    //     const deleteAction = (
    //     <GridActionsCellItem
    //         icon={<DeleteIcon/>}
    //         title="Delete"
    //         label="Delete"
    //         disabled
    //         // onClick={deleteSelectedAddress(params.row)}
    //     />
    //     );

    //     const actions: JSX.Element[] = [];

    //     // Push edit action if user has update permission.
    //     if (canUpdateAccount) {
    //     actions.push(editAction);
    //     }
    //     // Push edit action if user has delete permission.
    //     if (true) {
    //     actions.push(deleteAction);
    //     }
    //     return actions;
    //   }
    // }             
  ], [suppliers]);

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  const searchQuery = async (searchValue: string) => {
    // setIsLoading(true);
    setFilterQuery(searchValue);
  }

  // Handle page change
  const handlePageChange = (newPage: number) => {
    // page = newPage;
    setPage(newPage);
    setPageQueryString(`&page=${newPage}`);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageSizeQueryString(`&limit=${newPageSize}`);
  };

  return (
    <>


      <Box sx={{ display: "flex", justifyContent: "end", paddingTop: '8px', width: '100%' }}>
        <div style={{ display: "flex", flexDirection: 'row', justifyContent: 'flex-start', flex: 1, alignItems: "flex-start", textAlign: "start" }}>
          <TextField className='c-text-field-4'
            variant="standard"
            style={{ width: '40%', marginTop: 10 }}
            size='small'
            // value={filterQuery}
            placeholder='Search by Leagal Name, GST No, Phone, Email, ...'
            onChange={(e) => searchQuery(e.target.value.toString())}
            InputProps={{
              disableUnderline: true,
              startAdornment: <InputAdornment position="start"><SearchIcon style={{ marginTop: -3 }} size={20} /></InputAdornment>,
              style: {
                fontSize: 14,
                fontWeight: 'normal',
              },
            }}
          />
        </div>
        <div style={{ float: 'right', marginTop: 10, marginRight: 10 }}>
          <Button
            startIcon={<AddIcon />}
            size='small'
            onClick={(e) => handleOpenSupplierDrawer(e, false, undefined)}
            style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
            variant="contained"
          >
            Create New Supplier
          </Button>
        </div>
      </Box>


      <div style={{ height: "85vh", width: "100%", marginTop: '0px', overflow: 'auto' }}>
        {<CustomDataGrid
          rowCount={rowCount}
          rowsPerPageOptions={defaultRowsPerPageOptions}
          page={page}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
          columns={columns}
          rows={suppliers}
          getRowId={(row) => row._id}
          loading={status === ApiStatus.LOADING}
        />}
      </div>
      <DialogClientUpdateForm
        title={'Supplier'}
        open={openCustomerDrawer}
        onClose={handleCloseCustomerDrawer}
        onCancel={cancelDialog}
        selectedData={selectedCustomer}
        isClientEdit={isEdit}
      >
      </DialogClientUpdateForm>

    </>
  )
}

export default SupplierList;
